import { useEffect, useRef, useState } from 'react';

/**
 * Hook to track user activity and reset the inactivity timer
 * Listens for mouse, keyboard, and scroll events
 * Throttles activity recording to avoid excessive IPC calls
 */
const THROTTLE_MS = 30000; // Only record activity once every 30 seconds
const DEFAULT_ENABLED = false;

export function useActivityTracker() {
  const lastActivityRef = useRef<number>(0);
  const [enabled, setEnabled] = useState(DEFAULT_ENABLED);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI?.session) {
      return;
    }

    console.log('[useActivityTracker] Start');

    window.electronAPI.session
      ?.getInactivityMonitorEnabled?.()
      ?.then((result) => {
        if (result.success && typeof result.enabled === 'boolean') {
          setEnabled(result.enabled ?? DEFAULT_ENABLED);
        }
      })
      ?.catch((error: unknown) => {
        console.error(
          '[useActivityTracker] Failed to get inactivity monitor setting:',
          error,
        );
      });

    const cleanup = window.electronAPI.session?.onInactivityMonitorChanged?.(
      (data) => {
        setEnabled(data.enabled);
        console.log(
          '[useActivityTracker] Inactivity monitor state changed to:',
          data.enabled,
        );
      },
    );

    return () => {
      cleanup?.();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI?.session) {
      return;
    }

    if (!enabled) {
      console.log('[useActivityTracker] Activity monitor is disabled');
      return;
    }

    const recordActivity = () => {
      const now = Date.now();

      // Throttle: only record if 30 seconds have passed since last activity
      if (now - lastActivityRef.current < THROTTLE_MS) {
        return;
      }

      lastActivityRef.current = now;

      // Call the Electron IPC to record activity
      window.electronAPI.session.recordActivity().catch((error) => {
        console.error('[useActivityTracker] Failed to record activity:', error);
      });
    };

    // Listen to various user interaction events
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'click'];

    events.forEach((event) => {
      window.addEventListener(event, recordActivity, { passive: true });
    });

    // Record initial activity when component mounts
    recordActivity();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, recordActivity);
      });
    };
  }, [enabled]);
}
